import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Mail, ArrowLeft, KeyRound, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff, Sparkles } from "lucide-react";
import RudrasLogo from "../components/RudrasLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError } from "../lib/errors";
import PublicFooter from "../components/PublicFooter";

/**
 * Forgot Password page (Phase 1 — Feb 2026).
 *
 * Multi-step flow:
 *   1. EMAIL    — user enters their email + clicks Send code
 *   2. CODE     — 6-digit code entry + new password (with rule: ≥8 chars, ≥1 uppercase)
 *   3. DONE     — success message + "Go to login" CTA
 *
 * Reset codes are NEVER rendered here (Sep 2026 security correction):
 *   `/auth/forgot-password` no longer returns a reset code in any runtime,
 *   so there is nothing for this page to display and no banner to gate. A
 *   reset code opens an EXISTING account, so handing it to whoever asked
 *   for it was account takeover without mailbox access. The code now only
 *   ever reaches the user's inbox. See `backend/routes/auth.py`.
 *
 * Portal-aware:
 *   /forgot-password               -> customer portal
 *   /internal-admin/forgot-password -> internal admin portal
 *   The portal is detected from the URL pathname and sent to the backend so a
 *   customer can never reset an internal-admin password via the wrong route.
 */
export default function ForgotPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const portal = location.pathname.startsWith("/internal-admin") ? "internal" : "customer";
  const backLoginPath = portal === "internal" ? "/internal-admin/login" : "/login";

  const [step, setStep] = useState("email");      // "email" | "code" | "done"
  const [email, setEmail] = useState(search.get("email") || "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resentAt, setResentAt] = useState(0);

  // Auto-advance to step 2 if the user lands here with ?step=code (e.g., from an email link)
  useEffect(() => {
    if (search.get("step") === "code" && email) setStep("code");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function requestCode(isResend = false) {
    const e = (email || "").trim().toLowerCase();
    if (!e || !/^.+@.+\..+/.test(e)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    // Resend rate-limit: max one request every 15 seconds from the client
    // (the backend has its own anti-enumeration delays).
    if (isResend && Date.now() - resentAt < 15000) {
      toast.message("Please wait a few seconds before requesting another code.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: e, portal });
      setStep("code");
      setResentAt(Date.now());
      toast.success(isResend ? "If that email exists, a new code has been sent." : "If an account exists for this email, reset instructions have been sent.");
    } catch (err) {
      // Anti-enumeration: backend returns 200 even on unknown emails. A 4xx
      // here means a malformed request (e.g. empty email).
      toast.error(formatApiError(err, "Could not request a code. Try again."));
    } finally {
      setLoading(false);
    }
  }

  const passwordOk = newPassword.length >= 8 && /[A-Z]/.test(newPassword);

  async function applyReset() {
    const c = (code || "").trim();
    if (!/^\d{6}$/.test(c)) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    if (!passwordOk) {
      toast.error("Password must be at least 8 characters and contain at least one uppercase letter.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-with-code", {
        email: email.trim().toLowerCase(),
        code: c,
        new_password: newPassword,
        portal,
      });
      setStep("done");
      toast.success("Password updated. You can sign in now.");
    } catch (err) {
      toast.error(formatApiError(err, "Could not reset password. The code may have expired."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-emerald-50 via-white to-amber-50/30">
      <div className="flex-1 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md" data-testid="forgot-password-card">
        <Link to={backLoginPath} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-emerald-700 transition mb-4" data-testid="forgot-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to {portal === "internal" ? "internal admin" : "sign in"}
        </Link>
        <Card className="p-7 sm:p-8 shadow-xl border-emerald-100/60">
          <div className="flex items-center gap-2.5 mb-1.5">
            <RudrasLogo size="md" />
            <div className="font-display text-xl font-semibold text-slate-900">Rudras-AI</div>
          </div>
          {step === "email" && (
            <>
              <h1 className="font-display text-2xl font-semibold text-slate-900 mb-1.5">Forgot your password?</h1>
              <p className="text-sm text-slate-600 mb-5">Enter your email and we'll send you a 6-digit reset code.</p>
              <form onSubmit={(e) => { e.preventDefault(); requestCode(false); }} className="space-y-4">
                <div>
                  <Label htmlFor="fp-email" className="text-sm">Email</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium" data-testid="forgot-send-code">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4 mr-2" /> Send reset code</>}
                </Button>
              </form>
            </>
          )}

          {step === "code" && (
            <>
              <h1 className="font-display text-2xl font-semibold text-slate-900 mb-1.5">Check your inbox</h1>
              <p className="text-sm text-slate-600 mb-5">
                If an account exists for <span className="font-medium text-slate-900">{email}</span>, we&rsquo;ve sent a 6-digit reset code. It expires in 30 minutes. Didn&rsquo;t receive it within a minute? Check your spam folder or try resending.
              </p>
              <form onSubmit={(e) => { e.preventDefault(); applyReset(); }} className="space-y-4">
                <div>
                  <Label htmlFor="fp-code" className="text-sm">6-digit code</Label>
                  <Input
                    id="fp-code"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="font-mono text-center text-lg tracking-[0.4em]"
                    data-testid="forgot-code"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="fp-newpwd" className="text-sm">New password</Label>
                  <div className="relative">
                    <Input
                      id="fp-newpwd"
                      type={showPwd ? "text" : "password"}
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 8 chars, 1 uppercase"
                      data-testid="forgot-new-password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute inset-y-0 right-2 my-auto h-7 w-7 grid place-items-center text-slate-500 hover:text-slate-800"
                      tabIndex={-1}
                      aria-label={showPwd ? "Hide password" : "Show password"}
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="mt-1.5 flex items-center gap-3 text-[11.5px]">
                    <span className={`inline-flex items-center gap-1 ${newPassword.length >= 8 ? "text-emerald-700" : "text-slate-500"}`} data-testid="rule-length">
                      {newPassword.length >= 8 ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      At least 8 characters
                    </span>
                    <span className={`inline-flex items-center gap-1 ${/[A-Z]/.test(newPassword) ? "text-emerald-700" : "text-slate-500"}`} data-testid="rule-uppercase">
                      {/[A-Z]/.test(newPassword) ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      1 uppercase letter
                    </span>
                  </div>
                </div>
                <Button type="submit" disabled={loading || !passwordOk || code.length !== 6} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium" data-testid="forgot-apply">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</> : <><KeyRound className="w-4 h-4 mr-2" /> Set new password</>}
                </Button>
                <div className="text-center text-xs text-slate-500">
                  Didn't get the code?{" "}
                  <button type="button" onClick={() => requestCode(true)} className="text-emerald-700 hover:text-emerald-900 font-medium underline-offset-2 hover:underline" data-testid="forgot-resend">
                    Resend
                  </button>
                </div>
              </form>
            </>
          )}

          {step === "done" && (
            <div className="text-center" data-testid="forgot-done">
              <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-700" />
              </div>
              <h1 className="font-display text-2xl font-semibold text-slate-900 mb-1.5">Password updated</h1>
              <p className="text-sm text-slate-600 mb-5">You can now sign in with your new password.</p>
              <Button onClick={() => navigate(backLoginPath)} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium" data-testid="forgot-go-signin">
                Go to sign in
              </Button>
            </div>
          )}
        </Card>
        <div className="text-center text-xs text-slate-500 mt-4">
          Need help? <a href="mailto:support@rudras-ai.com" className="text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline">support@rudras-ai.com</a>
        </div>
      </div>
      </div>
      <PublicFooter />
    </div>
  );
}
